import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_FILE_SHARE_BYTES, isPreviewableMime, validFileShareSize } from '../utils/callFileShare';
const ROOT_PATH = '/';
const fmtSize = (n) => n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
const fileIcon = (mime, name) => {
    if (mime.startsWith('image/'))
        return '🖼️';
    if (mime.startsWith('video/'))
        return '🎬';
    if (mime.startsWith('audio/'))
        return '🎵';
    if (mime === 'application/pdf' || /\.pdf$/i.test(name))
        return '📄';
    return '📎';
};
// Storage entries sometimes lack a Content-Type (and the decrypted blob comes
// back untyped) — without a proper mime the receiver can't render a preview.
// Fall back to the file extension.
const EXT_MIME = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
    webp: 'image/webp', bmp: 'image/bmp', svg: 'image/svg+xml', avif: 'image/avif',
    heic: 'image/heic', mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
    m4v: 'video/mp4', mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg',
    m4a: 'audio/mp4', flac: 'audio/flac', pdf: 'application/pdf',
};
const effectiveMime = (mime, name) => {
    if (mime && mime !== 'application/octet-stream')
        return mime;
    const ext = name.split('.').pop()?.toLowerCase() || '';
    return EXT_MIME[ext] || mime;
};
const parentOf = (path) => {
    const trimmed = path.replace(/\/+$/, '');
    const idx = trimmed.lastIndexOf('/');
    return idx <= 0 ? ROOT_PATH : trimmed.slice(0, idx);
};
/**
 * "Share a file in the call" picker: browses the admin's own Dwinity storage
 * folder by folder (like the dashboard explorer), lets them share a file as
 * inline preview or as a download, download it to their own machine — or
 * share a local file instead.
 */
const CallFileSharePicker = ({ isOpen, onClose, adapter, onShare }) => {
    const { t } = useTranslation();
    const [cwd, setCwd] = useState(ROOT_PATH);
    const [allEntries, setAllEntries] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(null);
    const [notice, setNotice] = useState(null);
    const localInputRef = useRef(null);
    const [localFile, setLocalFile] = useState(null);
    useEffect(() => {
        if (!isOpen)
            return;
        let cancelled = false;
        setCwd(ROOT_PATH);
        setLocalFile(null);
        setNotice(null);
        setAllEntries(null);
        setLoadError(false);
        setSelected(null);
        adapter.listFiles()
            .then((list) => { if (!cancelled)
            setAllEntries(list); })
            .catch((err) => {
            console.error('[fileSharePicker] list failed:', err);
            if (!cancelled) {
                setAllEntries([]);
                setLoadError(true);
            }
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    // Current-folder view derived from the recursive listing: direct child
    // files of cwd, plus one folder row per distinct next path segment.
    const entries = React.useMemo(() => {
        if (allEntries === null)
            return null;
        const prefix = cwd === ROOT_PATH ? '/' : cwd.replace(/\/+$/, '') + '/';
        const files = [];
        const dirs = new Map();
        for (const e of allEntries) {
            const p = e.path.startsWith('/') ? e.path : '/' + e.path;
            if (!p.startsWith(prefix))
                continue;
            const rest = p.slice(prefix.length).replace(/\/+$/, '');
            if (!rest)
                continue;
            const slash = rest.indexOf('/');
            if (slash === -1 && !e.isDir) {
                files.push({ ...e, path: p, name: rest });
            }
            else {
                const dirName = slash === -1 ? rest : rest.slice(0, slash);
                const dirPath = prefix + dirName;
                if (!dirs.has(dirPath)) {
                    dirs.set(dirPath, { path: dirPath, name: dirName, size: 0, mime: '', isDir: true });
                }
            }
        }
        const byName = (a, b) => a.name.localeCompare(b.name);
        return [...[...dirs.values()].sort(byName), ...files.sort(byName)];
    }, [allEntries, cwd]);
    if (!isOpen)
        return null;
    const tooLargeMsg = t('mobile.chat.call.file_too_large', { mb: Math.round(MAX_FILE_SHARE_BYTES / (1024 * 1024)) });
    const rejectedMsg = t('mobile.chat.call.file_share_failed');
    const shareEntry = async (entry, mode) => {
        if (!validFileShareSize(entry.size)) {
            setNotice(tooLargeMsg);
            return;
        }
        setBusy({ path: entry.path, action: 'share' });
        setNotice(null);
        try {
            let file = await adapter.fetchFile(entry);
            // Storage listing sizes are not authoritative; check the decrypted content
            // before rewrapping or handing it to either transport.
            if (!validFileShareSize(file.size)) {
                setNotice(tooLargeMsg);
                return;
            }
            // Decrypted storage blobs often come back untyped — rewrap with the
            // real mime so the receiver can render the preview.
            const mime = effectiveMime(entry.mime || file.type, entry.name);
            if (mime && file.type !== mime) {
                file = new File([file], entry.name, { type: mime });
            }
            if (!onShare(file, mode))
                setNotice(rejectedMsg);
            else
                onClose(); // transfer progress is visible in the call tray
        }
        catch (err) {
            console.error('[fileSharePicker] fetch failed:', err);
            setNotice(t('mobile.chat.call.file_fetch_failed'));
        }
        finally {
            setBusy(null);
        }
    };
    const downloadEntry = async (entry) => {
        setBusy({ path: entry.path, action: 'download' });
        setNotice(null);
        try {
            const file = await adapter.fetchFile(entry);
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = entry.name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 30_000);
        }
        catch (err) {
            console.error('[fileSharePicker] download failed:', err);
            setNotice(t('mobile.chat.call.file_fetch_failed'));
        }
        finally {
            setBusy(null);
        }
    };
    const shareLocal = (mode) => {
        if (!localFile)
            return;
        if (!validFileShareSize(localFile.size)) {
            setNotice(tooLargeMsg);
            return;
        }
        if (!onShare(localFile, mode))
            setNotice(rejectedMsg);
        else
            onClose();
    };
    const modeButtons = (entry) => {
        const isBusy = busy?.path === entry.path;
        return (_jsxs("div", { className: "cfs-actions", children: [isPreviewableMime(effectiveMime(entry.mime, entry.name)) && (_jsx("button", { className: "cfs-btn cfs-btn-primary", disabled: !!busy, onClick: () => shareEntry(entry, 'preview'), children: busy?.path === entry.path && busy.action === 'share' ? '…' : `👁 ${t('mobile.chat.call.share_as_preview')}` })), _jsx("button", { className: "cfs-btn cfs-btn-primary", disabled: !!busy, onClick: () => shareEntry(entry, 'download'), children: isBusy && busy?.action === 'share' ? '…' : `📤 ${t('mobile.chat.call.share_as_download')}` }), _jsx("button", { className: "cfs-btn", disabled: !!busy, onClick: () => downloadEntry(entry), children: isBusy && busy?.action === 'download' ? '…' : `⬇ ${t('mobile.chat.call.download_to_me')}` })] }));
    };
    // Breadcrumb segments of the current folder.
    const crumbs = cwd === ROOT_PATH ? [] : cwd.replace(/^\/+|\/+$/g, '').split('/');
    return (_jsx("div", { className: "cfs-overlay", onClick: onClose, children: _jsxs("div", { className: "cfs-modal", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "cfs-header", children: [_jsxs("span", { className: "cfs-title", children: ["\uD83D\uDCE4 ", t('mobile.chat.call.share_file')] }), _jsx("button", { className: "cfs-close", onClick: onClose, children: "\u2715" })] }), _jsxs("div", { className: "cfs-breadcrumb", children: [_jsx("button", { className: "cfs-crumb", disabled: cwd === ROOT_PATH, onClick: () => setCwd(ROOT_PATH), children: "\uD83C\uDFE0" }), crumbs.map((seg, i) => (_jsxs(React.Fragment, { children: [_jsx("span", { className: "cfs-crumb-sep", children: "/" }), _jsx("button", { className: "cfs-crumb", disabled: i === crumbs.length - 1, onClick: () => setCwd('/' + crumbs.slice(0, i + 1).join('/')), children: seg })] }, i)))] }), notice && _jsxs("div", { className: "cfs-notice", children: ["\u26A0\uFE0F ", notice] }), _jsxs("div", { className: "cfs-body", children: [entries === null && _jsx("div", { className: "cfs-loading", children: t('mobile.chat.call.loading_files') }), entries !== null && loadError && _jsxs("div", { className: "cfs-loading", children: ["\u26A0\uFE0F ", t('mobile.chat.call.file_fetch_failed')] }), entries !== null && !loadError && entries.length === 0 && cwd === ROOT_PATH && (_jsx("div", { className: "cfs-loading", children: t('mobile.chat.call.no_files') })), entries !== null && (entries.length > 0 || cwd !== ROOT_PATH) && (_jsxs("ul", { className: "cfs-list", children: [cwd !== ROOT_PATH && (_jsx("li", { className: "cfs-item", children: _jsxs("button", { className: "cfs-item-row", onClick: () => setCwd(parentOf(cwd)), children: [_jsx("span", { className: "cfs-item-icon", children: "\u21A9\uFE0F" }), _jsx("span", { className: "cfs-item-name", children: ".." })] }) }, "..")), entries.map((f) => (_jsxs("li", { className: `cfs-item${selected?.path === f.path ? ' selected' : ''}`, children: [_jsxs("button", { className: "cfs-item-row", onClick: () => {
                                                if (f.isDir)
                                                    setCwd(f.path);
                                                else
                                                    setSelected(selected?.path === f.path ? null : f);
                                            }, children: [_jsx("span", { className: "cfs-item-icon", children: f.isDir ? '📁' : fileIcon(f.mime, f.name) }), _jsx("span", { className: "cfs-item-name", title: f.name, children: f.name }), !f.isDir && _jsx("span", { className: "cfs-item-size", children: fmtSize(f.size) })] }), !f.isDir && selected?.path === f.path && modeButtons(f)] }, f.path)))] }))] }), _jsxs("div", { className: "cfs-footer", children: [_jsx("input", { ref: localInputRef, type: "file", style: { display: 'none' }, onChange: (e) => {
                                const f = e.target.files?.[0] || null;
                                e.target.value = '';
                                setNotice(null);
                                if (f && !validFileShareSize(f.size)) {
                                    setNotice(tooLargeMsg);
                                    return;
                                }
                                setLocalFile(f);
                            } }), !localFile ? (_jsxs("button", { className: "cfs-btn", onClick: () => localInputRef.current?.click(), children: ["\uD83D\uDCBB ", t('mobile.chat.call.share_from_device')] })) : (_jsxs("div", { className: "cfs-local", children: [_jsxs("span", { className: "cfs-item-name", title: localFile.name, children: ["\uD83D\uDCBB ", localFile.name, " ", _jsxs("span", { className: "cfs-item-size", children: ["(", fmtSize(localFile.size), ")"] })] }), _jsxs("div", { className: "cfs-actions", children: [isPreviewableMime(localFile.type) && (_jsxs("button", { className: "cfs-btn cfs-btn-primary", onClick: () => shareLocal('preview'), children: ["\uD83D\uDC41 ", t('mobile.chat.call.share_as_preview')] })), _jsxs("button", { className: "cfs-btn cfs-btn-primary", onClick: () => shareLocal('download'), children: ["\uD83D\uDCE4 ", t('mobile.chat.call.share_as_download')] }), _jsx("button", { className: "cfs-btn", onClick: () => setLocalFile(null), children: "\u2715" })] })] }))] })] }) }));
};
export default CallFileSharePicker;
